import * as React from 'react';
import { Dispatch, ReactNode, SetStateAction, createContext, useEffect, useState } from "react";
import { LoginRequest, LoginResponse, ChangePasswordRequest } from "../api/Models";
import { deleteStoreValue, getStoreValue, storeValue, errorHandling } from '../utils/utils';
import { SUKIAPI } from '../api/Api';
import { useNavigate } from 'react-router-dom';
import * as jwtDecode from 'jwt-decode';
import { JwtPayload } from 'jwt-decode';

const ACCESS_TOKEN = 'suki-jwt_access'
const REFRESH_TOKEN = 'suki-jwt_refresh'
const USER_ROLE = 'suki-jwt_userRole'
const PARTNER_ID = 'suki-jwt_partnerID'

export interface ApplicationContext {
    authState: { token: string | null; refreshToken: string | null | undefined, authenticated: boolean | null},
    setAuthState: Dispatch<SetStateAction<any>>,
    onLogin: (req: LoginRequest) => Promise<LoginResponse | any>,
    onLogout: () => Promise<any>,
    onChangePassword: (req: ChangePasswordRequest) => Promise<any>,
    onUploadPartnersAwardFile: (req: any) => Promise<any>,
    onGetPartnersRecentUploads: () => Promise<any>,
    onGetPartnersTransactions: () => Promise<any>,
    onGetPartnersPointsValues: () => Promise<any>,
    onGetListOfPartners: () => Promise<any>,
    onDownloadPartnersReports: (req: any) => Promise<any>,
    onUploadOperationsAwardFile: (partnerId: any, req: any) => Promise<any>,
    onGetOperationsRecentUploads: () => Promise<any>,
    onGetOperationsTransactions: () => Promise<any>,
    onGetOperationsPointsValues: () => Promise<any>,
    onDownloadOperationsReports: (req: any, partnerId: any) => Promise<any>,
}

export const AppContext = createContext<ApplicationContext>({
    authState: {
        token: null,
        refreshToken: null,
        authenticated: null
    },
    setAuthState: () => {},
    onLogin: async (req: LoginRequest): Promise<LoginResponse | any> => {},
    onLogout: async (): Promise<any> => {},
    onChangePassword: async (req: ChangePasswordRequest): Promise<any> => {},
    onUploadPartnersAwardFile: async (req: any): Promise<any> => {},
    onGetPartnersRecentUploads: async (): Promise<any> => {},
    onGetPartnersTransactions: async (): Promise<any> => {},
    onGetPartnersPointsValues: async (): Promise<any> => {},
    onGetListOfPartners: async (): Promise<any> => {},
    onDownloadPartnersReports: async (req: any): Promise<any> => {},
    onUploadOperationsAwardFile: async (partnerId: any, req: any): Promise<any> => {},
    onGetOperationsRecentUploads: async (): Promise<any> => {},
    onGetOperationsTransactions: async (): Promise<any> => {},
    onGetOperationsPointsValues: async (): Promise<any> => {},
    onDownloadOperationsReports: async (req: any, partnerId: any): Promise<any> => {},
});

type AppContextProviderProps = {
    children: ReactNode;
};

export const AppContextProvider = ({ children }: AppContextProviderProps) => {
    const navigate = useNavigate();
    const [authState, setAuthState] = useState<{
        token: string | null,
        refreshToken: string | null | undefined,
        authenticated: boolean | null,
        userRole: string | null
    }>({
        token: '',
        refreshToken: '',
        authenticated: false,
        userRole: ''
    })

    const logout = React.useCallback(async () => {
        setAuthState({
            token: null,
            refreshToken: null,
            authenticated: null,
            userRole: null
        })
    
        await deleteStoreValue(ACCESS_TOKEN)
        await deleteStoreValue(REFRESH_TOKEN)
        await deleteStoreValue(USER_ROLE)
    
        localStorage.clear()
        navigate('/login');
    }, [setAuthState, navigate]);

    const checkTokenValidity = React.useCallback(async (refreshToken: any, accessToken: any) => {
        if (!accessToken) {
            await logout();
        } else {
            const decodedToken = jwtDecode.default(refreshToken) as JwtPayload | null;
    
            if (decodedToken && typeof decodedToken.exp === 'number') {
                const currentTime = Math.floor(Date.now() / 1000);

                if (decodedToken.exp < currentTime) {
                    await logout();
                }
            } 
        }
    }, [logout]);


    const loadToken = React.useCallback(async () => {
        const token = await getStoreValue(ACCESS_TOKEN);
        const refreshToken = await getStoreValue(REFRESH_TOKEN);
        const userRole = await getStoreValue(USER_ROLE);

        if (token) {
            setAuthState({
                token,
                refreshToken,
                authenticated: true,
                userRole,
            });

            const decodedToken = jwtDecode.default(token) as JwtPayload | null;

            if (decodedToken && typeof decodedToken.exp === 'number') {
                const currentTime = Math.floor(Date.now() / 1000);

                if (decodedToken.exp < currentTime) {
                    await logout()
                }
            }
        }
    }, [logout]);

    // START AUTH
    const login = async (request: LoginRequest) => {
        try {
            const response = await SUKIAPI.login(request) as LoginResponse
            setAuthState({ 
                token: response.access_token, 
                refreshToken: response.refresh_token, 
                authenticated: true,
                userRole: response.user_role
            })

            const decodedAccessToken = jwtDecode.default(response.access_token) as any | null;
            const { is_password_set: isPasswordSet } = response;
            
            await storeValue(ACCESS_TOKEN, response.access_token)
            await storeValue(REFRESH_TOKEN, response.refresh_token)
            await storeValue(USER_ROLE, response.user_role)
            await storeValue(PARTNER_ID, decodedAccessToken.suki_partner_id)
            
            localStorage.setItem('accountType', decodedAccessToken.suki_user_role);
            localStorage.setItem('isPasswordSet', isPasswordSet);
            localStorage.setItem('ACCESS_TOKEN', response.access_token)
            localStorage.setItem('REFRESH_TOKEN', response.refresh_token) 

            if(!decodedAccessToken.suki_user_role) {
                console.log("Invalid login. Account does not have a user role set.");
                return false;
            }

            return response
        } catch(error: any) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const changePassword = async (request: ChangePasswordRequest) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.createPassword(request, accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }
    // END AUTH

    // START PARTNERS
    const uploadPartnersAwardFile = async (file: File) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.uploadPartnersAwardFile(file, accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const downloadPartnersReports = async (date: any) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.downloadPartnersReports(date, accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getPartnersRecentUploads = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.getPartnersRecentUploads(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }
    
    const getPartnersTransactions = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.getPartnersTransactions(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getListOfPartners = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        if(!accessToken) {
            return
        }

        try {
            const response = await SUKIAPI.getListOfPartners(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getPartnersPointsValues = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        if(!accessToken) {
            return
        }

        try {
            const response = await SUKIAPI.getPartnersPointsValues(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }
    // END PARTNERS

    // START OPERATIONS
    const uploadOperationsAwardFile = async (partnerId: string, file: File) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.uploadOperationsAwardFile(partnerId, file, accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getOperationsRecentUploads = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.getOperationsRecentUploads(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getOperationsTransactions = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        if(!accessToken) {
            return
        }

        try {
            const response = await SUKIAPI.getOperationsTransactions(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const getOperationsPointsValues = async () => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        if(!accessToken) {
            return
        }

        try {
            const response = await SUKIAPI.getOperationsPointsValues(accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }

    const downloadOperationsReports = async (date: any, partnerId: any) => {
        const accessToken = await getStoreValue(ACCESS_TOKEN);

        try {
            const response = await SUKIAPI.downloadOperationsReports(date, partnerId, accessToken);
            return response;    
        } catch (error) {
            const parsedError = JSON.parse(error.message)
            const errorDetails = await errorHandling(parsedError);
            console.log({...errorDetails, hasError: true});
        }
    }
    // END OPERATIONS

    useEffect(() => {
        const fetchData = async () => {
            const accessToken = await getStoreValue(ACCESS_TOKEN);

            const refreshToken = localStorage.getItem('REFRESH_TOKEN')
            await loadToken();
            await checkTokenValidity(refreshToken, accessToken);
        };
    
        fetchData();
    }, [loadToken, checkTokenValidity]);
    
    const globalData = {
        authState,
        setAuthState,
        onLogin: login,
        onLogout: logout,
        onChangePassword: changePassword,
        onUploadPartnersAwardFile: uploadPartnersAwardFile,
        onGetPartnersRecentUploads: getPartnersRecentUploads,
        onGetPartnersTransactions: getPartnersTransactions,
        onGetPartnersPointsValues: getPartnersPointsValues,
        onGetListOfPartners: getListOfPartners,
        onDownloadPartnersReports: downloadPartnersReports,
        onUploadOperationsAwardFile: uploadOperationsAwardFile,
        onGetOperationsTransactions: getOperationsTransactions,
        onGetOperationsPointsValues: getOperationsPointsValues,
        onGetOperationsRecentUploads: getOperationsRecentUploads,
        onDownloadOperationsReports: downloadOperationsReports,
    };

    return (
        <AppContext.Provider value={globalData}>
            { children }
        </AppContext.Provider>
    )
}